'use client';

/**
 * Standardized Pill Component
 *
 * A reusable status indicator component with automatic icon mapping for common status types.
 * Perfect for health checks, workflow states, connection status, authentication states, and any other status displays.
 *
 * Features:
 * - Automatic icon mapping based on variant (checkmark for success, alert for error, etc.)
 * - Custom icon override support
 * - Flexible value-to-variant mapping via colorMap
 * - Uses centralized status color system
 * - Fully reusable across the entire platform
 *
 * Design Specification:
 * - Height: 31px minimum (comfortable touch target)
 * - Border radius: 9999px (fully rounded pill shape)
 * - Padding: 6px vertical × 12px horizontal
 * - Icon size: 18px × 18px (prominent and clear)
 * - Gap: 8px between icon and text
 * - Typography: 13px / 500 weight / 18px line-height
 * - Colors: Predefined status colors with automatic icon mapping
 *
 * @example
 * // Automatic icons based on variant
 * <Pill value="Active" variant="success" />  // ✓ Shows green checkmark
 * <Pill value="Error" variant="error" />    // ! Shows orange alert icon
 *
 * @example
 * // Custom icon override
 * import { Star } from 'lucide-react';
 * <Pill value="Featured" variant="success" icon={Star} />
 *
 * @example
 * // Dynamic variant mapping
 * <Pill
 *   value={status}
 *   colorMap={{ healthy: 'success', failed: 'error', pending: 'warning' }}
 * />
 *
 * @example
 * // No icons
 * <Pill value="Category" variant="info" showIcon={false} />
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { statusColors, type StatusVariant } from '@/lib/theme/status-colors';
import { typography } from '@/lib/theme';

export type PillVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'active' | 'custom';

interface PillProps {
  /** Display value (string, number, or any value that can be converted to string) */
  value: any;

  /**
   * Status variant - determines color and automatic icon
   * - success: Green background with checkmark (healthy, active, online, verified)
   * - error: Orange background with alert circle (failed, disconnected, invalid)
   * - warning: Yellow background with triangle (pending, needs attention, caution)
   * - info: Blue background with info icon (processing, neutral, default)
   * - neutral: Gray background (disabled, inactive, off)
   * - active: @deprecated Use 'success' instead (backward compatibility alias)
   * - custom: No default styling or icon
   */
  variant?: PillVariant;

  /**
   * Custom icon override
   * - Pass a Lucide icon component to override the automatic icon
   * - Pass null to explicitly disable icon for this pill
   * - Undefined uses automatic icon based on variant
   */
  icon?: LucideIcon | null;

  /**
   * Map string values to variants dynamically
   * Example: { 'healthy': 'success', 'auth_error': 'error', 'pending': 'warning' }
   */
  colorMap?: Record<string, PillVariant>;

  /**
   * Show/hide icons globally (default: true)
   * Set to false to disable all icons for this pill
   */
  showIcon?: boolean;

  /**
   * Size variant
   * - sm: Smaller pill (24px height)
   * - default: Standard pill (31px height)
   * - lg: Larger pill (36px height)
   */
  size?: 'sm' | 'default' | 'lg';

  /** Additional CSS classes */
  className?: string;
}

// Variant style definitions using shared color palette
const variantStyles: Record<PillVariant, React.CSSProperties> = {
  success: {
    backgroundColor: statusColors.success.bg,
    color: statusColors.success.text,
  },
  error: {
    backgroundColor: statusColors.error.bg,
    color: statusColors.error.text,
  },
  warning: {
    backgroundColor: statusColors.warning.bg,
    color: statusColors.warning.text,
  },
  info: {
    backgroundColor: statusColors.info.bg,
    color: statusColors.info.text,
  },
  neutral: {
    backgroundColor: statusColors.neutral.bg,
    color: statusColors.neutral.text,
  },
  // Backward compatibility alias
  active: {
    backgroundColor: statusColors.success.bg,
    color: statusColors.success.text,
  },
  custom: {
    // No default styling for custom variant
  },
};

// Automatic icon mapping for status variants
const defaultIcons: Record<PillVariant, LucideIcon | null> = {
  success: CheckCircle2,     // ✓ Checkmark for success/healthy/active
  error: AlertCircle,        // ! Alert circle for errors/failures
  warning: AlertTriangle,    // ⚠ Triangle for warnings/caution
  info: Info,                // i Info icon for informational states
  neutral: null,             // No icon for neutral state by default
  active: CheckCircle2,      // ✓ Backward compatibility (same as success)
  custom: null,              // No default icon for custom variant
};

// Size configurations
const sizeConfig = {
  sm: {
    height: '24px',
    paddingX: '10px',
    paddingY: '4px',
    iconSize: '14px',
    fontSize: '12px',
    gap: '6px',
  },
  default: {
    height: '31px',
    paddingX: '12px',
    paddingY: '6px',
    iconSize: '18px',
    fontSize: '13px',
    gap: '8px',
  },
  lg: {
    height: '36px',
    paddingX: '14px',
    paddingY: '8px',
    iconSize: '20px',
    fontSize: '14px',
    gap: '10px',
  },
};

// Base typography style from central design tokens
const baseTypographyStyle: React.CSSProperties = typography.ui.pill;

export const Pill: React.FC<PillProps> = ({
  value,
  variant = 'info',
  icon,
  colorMap,
  showIcon = true,
  size = 'default',
  className,
}) => {
  const displayValue = value != null ? String(value) : '-';

  // Map value to variant if colorMap provided
  const resolvedVariant = colorMap?.[displayValue] ?? variant;

  // Determine which icon to use:
  // 1. If icon explicitly set (including null), use it
  // 2. Otherwise, use default icon for variant if showIcon is true
  const IconComponent = icon !== undefined
    ? icon
    : (showIcon ? defaultIcons[resolvedVariant] : null);

  // Get icon color from status colors
  const getIconColor = (v: PillVariant): string | undefined => {
    // Handle deprecated 'active' variant
    const normalizedVariant = v === 'active' ? 'success' : v;

    // Return icon color for standard variants
    if (normalizedVariant === 'success') return statusColors.success.icon;
    if (normalizedVariant === 'error') return statusColors.error.icon;
    if (normalizedVariant === 'warning') return statusColors.warning.icon;
    if (normalizedVariant === 'info') return statusColors.info.icon;
    if (normalizedVariant === 'neutral') return statusColors.neutral.icon;

    // Custom variant uses text color
    return variantStyles[v].color?.toString();
  };

  const iconColor = getIconColor(resolvedVariant);
  const sizeStyles = sizeConfig[size];

  // Merge base typography with variant styles and size
  const pillStyle: React.CSSProperties = {
    ...baseTypographyStyle,
    ...variantStyles[resolvedVariant],
    fontSize: sizeStyles.fontSize,
    minHeight: sizeStyles.height,
    paddingLeft: sizeStyles.paddingX,
    paddingRight: sizeStyles.paddingX,
    paddingTop: sizeStyles.paddingY,
    paddingBottom: sizeStyles.paddingY,
    gap: sizeStyles.gap,
  };

  return (
    <div className="inline-flex items-center justify-center">
      <div
        data-pill={resolvedVariant}
        className={cn(
          'inline-flex items-center w-fit',
          className
        )}
        style={{
          ...pillStyle,
          borderRadius: '9999px',  // Fully rounded pill shape
        }}
      >
        {IconComponent && (
          <IconComponent
            style={{
              width: sizeStyles.iconSize,
              height: sizeStyles.iconSize,
              flexShrink: 0,
              color: iconColor,  // Match status color icon
            }}
          />
        )}
        <span className="whitespace-nowrap">{displayValue}</span>
      </div>
    </div>
  );
};
